
import { IKeywordVote } from "api/types/_campaign";
import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from "common/toast";
import Hashtag from "components/Common/Hashtag";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import { UPLOAD_ACTION, VOTE_KEYWORD_IMAGE_TYPE } from "helpers/constans";
import React, { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Col, Input, Label, Modal, ModalBody, ModalHeader, Row, Spinner } from "reactstrap";
import { postKeywordVote, putKeywordVote } from "store/campaign/thunk";
import { postUploadFileMulti } from "store/thunks";
import * as Yup from "yup";

export interface Props {
    isOpen?: boolean;
    detail?: IKeywordVote | null,
    onClose?: () => void
    triggerRefresh?: (type?: string) => void;
}

registerLocale("en", en);
registerLocale("ko", ko);

const FILE_SIZE = 11 * 1024 * 1024; // 11MB
const SUPPORTED_FORMATS = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];

const KeywordForm = ({
    isOpen = false,
    detail = null,
    onClose,
    triggerRefresh
}: Props) => {
    const { t, i18n } = useTranslation();

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const onCloseClick = () => {
        onClose && onClose();
    };

    const handleSubmit = async (values: any) => {
        try {
            setIsLoading((_prev) => true);
            let res_files: any = [];
            if (values?.files?.length) {
                const res: any = await postUploadFileMulti({ files: values?.files, action: UPLOAD_ACTION.VOTE_KEYWORD });
                res_files = (res?.data || [])?.map((item: any) => ({
                    image_url: item?.filepath || '',
                    thumbnail_url: "",
                    type: VOTE_KEYWORD_IMAGE_TYPE.PRESS_AI
                }));
            };
            const _image_urls: string[] = (values?.image_urls || [])?.map((img: any) => ({
                image_url: img?.type === VOTE_KEYWORD_IMAGE_TYPE.PRESS_AI ? img?.image_filepath : (img?.image_url || ''),
                thumbnail_url: img?.type === VOTE_KEYWORD_IMAGE_TYPE.PRESS_AI ? '' : (img?.thumbnail_url || ''),
                type: img?.type
            }));
            const data = {
                tags: (values?.tags || [])?.map((_tag: any) => `${_tag?.text || ''}`),
                images: [...(_image_urls), ...(res_files || [])],
            };
            const response: any = !!detail?.keyword_id ? await putKeywordVote(detail?.keyword_id, data) : await postKeywordVote(data);
            if (response?.code === 200) {
                setIsLoading((_prev) => false);
                toast(
                    `${t("The process has been completed.")}`,
                    CONFIG_OPTION_TOAST_NORMAL
                );
                formik.setErrors({});
                triggerRefresh && triggerRefresh(!!values?.is_show_create_reporter ? 'REFRESH_MEDIA' : '');
            } else {
                setIsLoading((_prev) => false);
                toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
            }
        } catch (error: any) {
            setIsLoading((_prev) => false);
            toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
            return error;
        }
    };

    const validationSchema = Yup.object({
        keyword: Yup.string().required(`${t("This is required")}`),
        tags: Yup.array().of(Yup.object().shape({
            id: Yup.string(),
            text: Yup.string(),
        })).nullable(),
        image_urls: Yup.array().of(Yup.object().shape({
            image_url: Yup.string(),
            image_filepath: Yup.string(),
            thumbnail_url: Yup.string(),
            type: Yup.number(),
        })).nullable(),

        files: Yup.array()
            .of(
                Yup.mixed()
                    .test(
                        'fileSize',
                        t('File too large. Max size is 10MB.'),
                        (value: any) => !value || (value?.size <= FILE_SIZE)
                    )
                    .test(
                        'fileFormat',
                        t('Unsupported Format. Please upload an Image file.'),
                        (value: any) => !value || SUPPORTED_FORMATS.includes(value?.type)
                    )
            )
            .when("image_urls", {
                is: (image_urls: any) => !image_urls || image_urls?.length === 0,
                then: (schema) => schema.min(1, t('Please select at least 1 image')),
                otherwise: (schema) => schema.notRequired(),
            }),
    });

    const formik = useFormik({
        initialValues: {
            keyword: '',
            tags: [],
            image_urls: [],
            files: [],
        },
        validationSchema,
        onSubmit: handleSubmit,
    });

    const handleChangeFile = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            const fileArray = Array.from(files);
            formik.setFieldValue("files", [...formik.values.files, ...fileArray]);
        } else {
            formik.setFieldValue("files", []);
        }
    };

    const handleDeleteFile = (index: number) => {
        if (isLoading) {
            return;
        }
        const fileArray = formik.values.files?.filter((f, i) => i !== index);
        formik.setFieldValue("files", fileArray);
    }

    const handleDeleteImageUrl = (index: number) => {
        if (isLoading) {
            return;
        }
        const imageArray = formik.values.image_urls?.filter((f, i) => i !== index);
        formik.setFieldValue("image_urls", imageArray);
    }

    useEffect(() => {
        const handleCallAllOption = async () => {
            try {
                if (!!detail?.keyword_id) {
                    const _keyword = detail?.keyword || '';
                    const _tags = (detail?.tags || [])?.map((_tag: any) => ({ id: `${_tag?.id}`, text: _tag?.name }));
                    const _image_urls = detail?.images || [];
                    setTimeout(() => {
                        formik.setFieldValue('keyword', _keyword);
                        formik.setFieldValue('tags', _tags);
                        formik.setFieldValue('image_urls', _image_urls);
                        formik.setFieldValue('files', []);
                    }, 300);
                } else {
                    formik.setFieldValue('keyword', '');
                    formik.setFieldValue('tags', []);
                    formik.setFieldValue('image_urls', []);
                    formik.setFieldValue('files', []);
                }
                setTimeout(() => {
                    formik.setErrors({});
                }, 400);
            } catch (error: any) {
                // setIsLoadingDetail((_prev) => false);
                return error;
            }
        };
        handleCallAllOption();
    }, [JSON.stringify(detail)]);

    return (
        <React.Fragment>
            <Modal
                isOpen={isOpen}
                centered={true}
                size="lg"
                className={`border-header-footer`}
                scrollable={false}
                toggle={onCloseClick}
                keyboard={true}
            >
                <ModalHeader toggle={onCloseClick} className="modal-title-flex-grow-1">
                    <div className="d-flex flex-column gap-0 gap-sm-2 fs-18 fs-md-20">
                        <div className="flex-grow-1">
                            <>{!!detail?.keyword_id ? t('Button Update Keyword') : t('Button Create Keyword')}</>
                        </div>
                    </div>
                </ModalHeader>
                <ModalBody className="position-relative">
                    <div>
                        <form onSubmit={formik.handleSubmit}>
                            <Row>

                                <Col sm={12} className="mb-3">
                                    <Label htmlFor="keyword" className="form-label">{t('Keyword')} <span className="text-danger"> *</span></Label>
                                    <Input
                                        name="keyword"
                                        type="text"
                                        className="form-control"
                                        id="keyword"
                                        placeholder={t('Keyword')}
                                        value={formik?.values?.keyword || ''}
                                        onChange={(event: any) => formik.setFieldValue('keyword', event?.target?.value)}
                                        disabled={true}
                                    />
                                    {formik.touched.keyword && formik.errors.keyword ? (
                                        <div className="text-danger mt-2">{formik.errors.keyword}</div>
                                    ) : null}
                                </Col>

                                <Col sm={12} md={12} xl={12} className="mb-3">
                                    <div>
                                        <div style={{ minHeight: '80px' }}>
                                            <label className="form-label">
                                                {t("Tags")}
                                            </label>
                                            <Hashtag
                                                name="tags"
                                                placeholder={`${t("Press Enter to add a new tag")}...`}
                                                suggestions={[]}
                                                initialValue={formik?.values?.tags || []}
                                                onChangeTag={(event: any) => {
                                                    formik.setFieldValue('tags', event)
                                                }}
                                                idSelected="hashtags-CampaignFormMulti"
                                                maxHashtags={2000}
                                                maxHeight="150px"
                                            />
                                        </div>
                                    </div>
                                </Col>

                                <Col sm={12} md={12} xl={12} className="mb-4">
                                    <Label htmlFor="formSizeDefault" className="form-label">{t('Image')} <span className="text-danger"> *</span></Label>
                                    <div className="d-flex gap-3 flex-wrap">
                                        {
                                            (formik.values.image_urls || [])?.map((img: any, index: number) => (
                                                <div className="position-relative" style={{ width: '200px', height: '200px', backgroundColor: '#eaeaea', overflow: 'hidden', borderRadius: 'var(--vz-border-radius)' }}>
                                                    <img
                                                        src={img?.thumbnail_url}
                                                        alt={`preview-${index}`}
                                                        style={{ width: "100%", height: "100%", objectFit: 'cover' }}
                                                    />
                                                    <button type="button" className="position-absolute btn btn-sm btn-soft-danger edit-item-btn"
                                                        style={{
                                                            top: '10px',
                                                            right: '10px',
                                                            width: '30px',
                                                            height: '30px',
                                                            borderRadius: '15px',
                                                        }}
                                                        onClick={() => handleDeleteImageUrl(index)}
                                                    ><i className="ri-delete-bin-3-fill align-bottom"></i></button>
                                                </div>
                                            ))
                                        }
                                        {
                                            (formik.values.files || [])?.map((file: any, index: number) => (
                                                <div className="position-relative" style={{ width: '200px', height: '200px', backgroundColor: '#eaeaea', overflow: 'hidden', borderRadius: 'var(--vz-border-radius)' }}>
                                                    <img
                                                        src={URL.createObjectURL(file)}
                                                        alt={`preview-${index}`}
                                                        style={{ width: "100%", height: "100%", objectFit: 'cover' }}
                                                    />
                                                    <button type="button" className="position-absolute btn btn-sm btn-soft-danger edit-item-btn"
                                                        style={{
                                                            top: '10px',
                                                            right: '10px',
                                                            width: '30px',
                                                            height: '30px',
                                                            borderRadius: '15px',
                                                        }}
                                                        onClick={() => handleDeleteFile(index)}
                                                    ><i className="ri-delete-bin-3-fill align-bottom"></i></button>
                                                </div>
                                            ))
                                        }
                                        <div className="custom-file-image-button">
                                            <Input
                                                className="form-control"
                                                accept="image/jpeg, image/png, image/gif, image/webp, image/svg+xml"
                                                label="Upload Your Image"
                                                data-title="Upload Your Image"
                                                id="formSizeDefault"
                                                type="file"
                                                multiple
                                                disabled={isLoading}
                                                onChange={handleChangeFile}
                                            />
                                            {<div className="text-upload" style={{ zIndex: 1 }}>  <i className="ri-upload-line me-2" /> {formik.values.files?.length ? t('Add Image') : t('No file chosen')}</div>}
                                        </div>
                                    </div>
                                    {formik.touched.files && formik.errors.files ? (
                                        <div className="text-danger mt-2">{formik.errors.files}</div>
                                    ) : null}
                                </Col>

                                <Col sm={12} className="mb-2" >
                                    <div className="d-flex gap-3 justify-content-end align-item-center">
                                        <button
                                            className="btn btn-primary fs-14 px-1 d-flex justify-content-center align-item-center"
                                            type="submit"
                                            style={{ width: '160px' }}
                                            disabled={isLoading}
                                        >
                                            {isLoading && <span style={{ transform: 'translateY(1px)' }} className="me-1"><Spinner size="sm"></Spinner></span>}<span>{
                                                !!detail?.keyword_id ? t('Button Update Keyword') : t('Button Create Keyword')
                                            }</span>
                                        </button>
                                        <button
                                            className="btn btn-secondary fs-14 px-1 d-flex justify-content-center align-item-center"
                                            type="button"
                                            onClick={onCloseClick}
                                            style={{ width: '75px' }}
                                        >
                                            <i className="ri-close-fill" style={{ lineHeight: '22px' }} /> <span className="ms-1">{t("Button Close")}</span>
                                        </button>
                                    </div>
                                </Col>
                            </Row>
                        </form>
                    </div>
                </ModalBody>
            </Modal>
        </React.Fragment >
    );
};

export default KeywordForm;

